import React, { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { websiteOrderService } from "@/api/websiteOrderService"
import { Link } from "react-router-dom"
import { formatToDevanagariStyle } from "@/utilities/helper"
import { toast } from "sonner"
import ConfirmDialog from "@/components/Dialog/ConfirmDialog"
import { ReturnRequestDialog } from "../components/ReturnRequestDialog"

export const Orders: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["orders", "website"],
    queryFn: ({ signal }) => websiteOrderService.getOrders(signal),
  })

  const orders = (data as any)?.data?.data || []
  const queryClient = useQueryClient()
  const [orderToCancel, setOrderToCancel] = useState<any>(null)
  const [orderToReturn, setOrderToReturn] = useState<any>(null)
  const requestReturn = useMutation({
    mutationFn: ({ orderId, ...body }: any) =>
      websiteOrderService.requestReturn(orderId, body),
    onSuccess: () => {
      toast.success("Return request sent")
      setOrderToReturn(null)
    },
    onError: (error: any) =>
      toast.error(error?.message ?? "Unable to send this request"),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["orders", "website"] }),
  })
  const cancelOrder = useMutation({
    mutationFn: (orderId: number) => websiteOrderService.cancelOrder(orderId),
    onSuccess: () => toast.success("Order cancelled"),
    onError: (error: any) =>
      toast.error(error?.message ?? "Unable to cancel this order"),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["orders", "website"] }),
  })

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen py-10 flex items-center justify-center">
        <div className="text-gray-500">Loading orders...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-red-500">Failed to load orders</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Order History</h1>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">No orders found</p>
            <Link
              to="/"
              className="mt-4 inline-block bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order: any) => {
              const orderTotal = order.total_amount || 0
              const orderDate = new Date(
                order.order_date || order.created_at
              ).toLocaleDateString()

              return (
                <div
                  key={order.id}
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order #{order.code || order.id}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Placed on {orderDate}
                      </p>
                      <p className="text-sm text-gray-500">
                        Status:{" "}
                        <span className="font-medium">
                          {order.status_label || "Pending"}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Delivery Status:{" "}
                        <span className="font-medium">
                          {order.delivery_status_label || "Pending"}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Payment Status:{" "}
                        <span className="font-medium">
                          {order.payment_status_label || "Pending"}
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        Rs. {formatToDevanagariStyle(orderTotal)}
                      </p>
                      {/* TODO: make the page to show the order details  */}
                      {/* <Link
                        to={`/orders/${order.id}`}
                        className="text-sm text-indigo-600 hover:text-indigo-700 mt-2 inline-block"
                      >
                        View Details
                      </Link> */}
                      {order.status_parsed === "Pending" && (
                        <button
                          type="button"
                          disabled={cancelOrder.isPending}
                          onClick={() => setOrderToCancel(order)}
                          className="mt-2 block ml-auto text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                          Cancel Order
                        </button>
                      )}
                      {order.can_request_return && (
                        <button
                          type="button"
                          onClick={() => setOrderToReturn(order)}
                          className="mt-2 block ml-auto text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          Request Return
                        </button>
                      )}
                    </div>
                  </div>

                  {order.return_requests?.map((request: any) => (
                    <p key={request.id} className="text-sm text-gray-500 mt-2">
                      Return request ({request.created_at}):{" "}
                      <span className="font-medium">
                        {request.status_label}
                      </span>
                    </p>
                  ))}

                  {order.order_items && order.order_items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ul className="divide-y divide-gray-100">
                        {order.order_items.map((item: any) => (
                          <li
                            key={item.id}
                            className="flex items-center gap-4 py-3"
                          >
                            <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                              {item?.image ? (
                                <div className="w-full h-full">
                                  <img
                                    src={item.image.url}
                                    alt={item.name}
                                    className="w-full h-full object-cover rounded-md"
                                  />
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  No Image
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 line-clamp-2">
                                {item.name}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                SKU: {item.sku}
                              </p>
                              <p className="text-sm text-gray-600 mt-0.5">
                                Rs. {formatToDevanagariStyle(item.sell_price)} ×{" "}
                                {item.quantity}
                                {Number(item.discount) > 0 && (
                                  <span className="ml-2 text-xs text-green-600">
                                    −Rs.{" "}
                                    {formatToDevanagariStyle(item.discount)}{" "}
                                    discount
                                  </span>
                                )}
                              </p>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                              Rs. {formatToDevanagariStyle(item.total)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      <ReturnRequestDialog
        order={orderToReturn}
        isSaving={requestReturn.isPending}
        onClose={() => setOrderToReturn(null)}
        onSubmit={(items, notes) =>
          requestReturn.mutate({ orderId: orderToReturn.id, items, notes })
        }
      />
      <ConfirmDialog
        open={Boolean(orderToCancel)}
        title="Cancel this order?"
        description={`Order #${orderToCancel?.code ?? ""} will be cancelled. This cannot be undone.`}
        closeText="Keep Order"
        confirmText="Cancel Order"
        onClose={() => setOrderToCancel(null)}
        onConfirm={() => {
          cancelOrder.mutate(orderToCancel.id)
          setOrderToCancel(null)
        }}
      />
    </div>
  )
}
